/**
 * DEC3 option B: which metric each objective's Result counts, per workspace
 * (docs/superpowers/specs/2026-10-13-result-mapping-dec3-design.md §0, §2).
 * Pure. api/_lib imports this module (lookerTargets, lookerResultMapping,
 * lookerTenancy): relative imports only, never '@/' — vite.config.ts loads that
 * server code without the alias, and an '@/' import fails `pnpm build`.
 *
 * @module services/targets/resultMetric
 */
import type { TargetObjective } from './targetRules';

export type ResultMetric = 'impressions' | 'reach' | 'clicks' | 'conversions';
export type AwarenessMetric = 'impressions' | 'reach';
export const AWARENESS_METRICS: readonly AwarenessMetric[] = ['impressions', 'reach'];
/** No mapping row = this. */
export const DEFAULT_AWARENESS: AwarenessMetric = 'impressions';

/** The central definition. */
export const DEFAULT_METRIC: Readonly<Record<TargetObjective, ResultMetric>> = {
  awareness: DEFAULT_AWARENESS, consideration: 'clicks', conversion: 'conversions',
};
/** Spec §0. Widening it is a CHECK change in migration-land and a line here. */
export const ALLOWED_METRICS: Readonly<Record<TargetObjective, readonly ResultMetric[]>> = {
  awareness: AWARENESS_METRICS, consideration: ['clicks'], conversion: ['conversions'],
};

export const isAwarenessMetric = (v: unknown): v is AwarenessMetric =>
  typeof v === 'string' && (AWARENESS_METRICS as readonly string[]).includes(v);

/** The metric an objective's Result counts under the workspace's Awareness choice. */
export const metricFor = (objective: TargetObjective, awareness: AwarenessMetric): ResultMetric =>
  (objective === 'awareness' ? awareness : DEFAULT_METRIC[objective]);

/** What the server knew about the mapping for one dashboard load (payload.resultMapping). */
export interface ResultMappingInfo {
  /** null exactly when `failed` (R5): never a definition nobody chose. */
  awareness: AwarenessMetric | null;
  /** The workspace chose something other than the central default. */
  overridden: boolean;
  /** The mapping could not be read: Result cells are NULL and targets are 'failed'. */
  failed: boolean;
  /** No workspace mapping applies (all workspaces, the builder preview): the central default is in force. */
  central: boolean;
}

/** Frozen: both flow into every payload that uses them; a mutation would change every later load. */
export const CENTRAL_MAPPING: Readonly<ResultMappingInfo> = Object.freeze({ awareness: DEFAULT_AWARENESS, overridden: false, failed: false, central: true });
export const FAILED_MAPPING: Readonly<ResultMappingInfo> = Object.freeze({ awareness: null, overridden: false, failed: true, central: false });
export const workspaceMapping = (awareness: AwarenessMetric): ResultMappingInfo =>
  ({ awareness, overridden: awareness !== DEFAULT_AWARENESS, failed: false, central: false });

/**
 * DEC3 §4.3's badge "นิยามของ workspace: Reach": an Awareness row while the workspace itself chose Reach, whatever its
 * Result reads; never another objective, a central default, nor while the definition loads or fails. /overview's
 * boxes 9 and 13 share this one gate (rules-04 C review I1).
 */
export const reachBadgeShown = (objective: string, mapping: ResultMappingInfo | null): boolean =>
  objective === 'awareness' && mapping !== null && mapping.overridden && mapping.awareness === 'reach';

/** One workspace_result_mappings row. */
export interface ResultMappingRow {
  id: string;
  workspaceId: string;
  objective: 'awareness';
  metric: AwarenessMetric;
  status: 'active' | 'superseded';
  setBy: string;
  setAt: string;
}

/** The Awareness metric in force. The unique index forbids two active rows; if a read shows two, fail loudly. */
export const currentAwareness = (rows: readonly ResultMappingRow[]): AwarenessMetric => {
  const active = rows.filter((r) => r.objective === 'awareness' && r.status === 'active');
  if (active.length > 1) throw new Error('result mapping: more than one active row');
  return active[0]?.metric ?? DEFAULT_AWARENESS;
};

export interface MappingChange { id: string; setBy: string; setAt: string; from: AwarenessMetric; to: AwarenessMetric }

const bySetAt = (a: ResultMappingRow, b: ResultMappingRow): number =>
  (a.setAt < b.setAt ? -1 : a.setAt > b.setAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Every version as from → to, newest first; the first version replaced the default. */
export const mappingHistory = (rows: readonly ResultMappingRow[]): MappingChange[] => {
  const asc = rows.filter((r) => r.objective === 'awareness').slice().sort(bySetAt);
  return asc
    .map((r, i) => ({ id: r.id, setBy: r.setBy, setAt: r.setAt, from: i === 0 ? DEFAULT_AWARENESS : asc[i - 1].metric, to: r.metric }))
    .reverse();
};
